<template>
  <modal
    ref="leadDialog"
    name="lead-flow-workspace"
    :width="'98%'"
    :height="'96%'"
    :max-width="1920"
    :scrollable="true"
    :adaptive="true"
    :click-to-close="!modalPending && !aiOpen"
    :reset="true"
    class="lead-flow-workspace-modal modal-not-overflow"
    :class="{ 'lead-flow-workspace-modal--ai': aiOpen }"
    @opened="onOpened"
    @before-close="onBeforeClose"
  >
    <div
      v-if="validLeadId"
      ref="workspace"
      class="lead-flow-modal"
      role="document"
    >
      <LeadFlowModalHeader
        :detail="detail"
        :lead-id="validLeadId"
        :loading="detailLoading"
        :trengo-sync-pending="trengoSyncPending"
        @ask-ai="$emit('ask-ai')"
        @sync-trengo="syncTrengoTail"
        @refresh="refreshWorkspace"
        @close="close"
      />

      <LeadAssignManager
        v-if="detail && detail.can_assign_manager && !detail.manager"
        :lead-id="validLeadId"
        @pending-change="assignmentPending = $event"
        @assigned="onManagerAssigned"
      />
      <div class="lead-flow-modal__body">
        <div v-if="detailLoading && !detail" class="lead-flow-modal__initial-state lead-flow-modal__contactability-state" aria-busy="true">
          Loading lead details...
        </div>
        <section v-else-if="detailError" class="lead-flow-modal__initial-state lead-flow-modal__detail-error" role="alert">
          <strong>Unable to load lead details.</strong>
          <p>The lead may be unavailable or you may not have access to it.</p>
          <button type="button" @click="loadDetail(validLeadId)">Retry</button>
        </section>
        <LeadFlowWorkspace
          v-if="detail && !detailError"
          :key="validLeadId"
          ref="flowWorkspace"
          :lead-id="validLeadId"
          @board-reconcile="$emit('board-reconcile', $event)"
          @last-activity="updateLastActivity"
          @stage-change="updateCurrentStage"
          @pending-change="workspacePending = $event"
        >
          <template #overview>
            <div class="lead-flow-modal__overview">
              <LeadPersonalDetails
                :personal="detail.personal_details || {}"
              />
              <LeadTrengoOrigin v-if="detail.trengo_origin" :key="validLeadId" :origin="detail.trengo_origin" :lead-id="validLeadId" @refresh="refreshWorkspace" />
              <LeadContactability
                v-if="contactability"
                :contactability="contactability"
              />
              <div
                v-else-if="contactabilityLoading"
                class="lead-flow-modal__contactability-state"
                aria-busy="true"
              >
                Loading contactability...
              </div>
              <section
                v-else-if="contactabilityError"
                class="lead-flow-modal__contactability-state lead-flow-modal__contactability-state--error"
                role="alert"
              >
                <strong>Unable to load contactability.</strong>
                <button type="button" @click="loadContactability(validLeadId)">Retry</button>
              </section>
              <LeadOverview
                class="lead-flow-modal__overview-details"
                :detail="detail"
                :loading="detailLoading"
                @guidance-changed="onGuidanceChanged"
              />
            </div>
          </template>
          <template #communication-after>
            <LeadDoubleRequests
              v-if="doubleRequestsError || (doubleRequests && doubleRequests.count)"
              :items="doubleRequests ? doubleRequests.results : []"
              :count="doubleRequests ? doubleRequests.count : 0"
              :error="Boolean(doubleRequestsError)"
              @retry="loadDoubleRequests(validLeadId)"
            />
          </template>
        </LeadFlowWorkspace>
      </div>

    </div>
  </modal>
</template>

<script>
import {
  getLeadFlowContactability,
  getLeadFlowDetail,
  getLeadFlowLeadDoubleRequests,
  postLeadFlowTrackerTrengoTailSync
} from '@/http'
import LeadAssignManager from '@/views/crm/task-tracker/LeadAssignManager.vue'
import LeadContactability from '@/views/crm/leads/detail/Contactability.vue'
import LeadDoubleRequests from '@/views/crm/task-tracker/LeadDoubleRequests.vue'
import LeadFlowModalHeader from '@/views/crm/task-tracker/LeadFlowModalHeader.vue'
import LeadFlowWorkspace from '@/views/crm/task-tracker/LeadFlowWorkspace.vue'
import LeadOverview from '@/views/crm/task-tracker/LeadOverview.vue'
import LeadPersonalDetails from '@/views/crm/task-tracker/LeadPersonalDetails.vue'
import LeadTrengoOrigin from '@/views/crm/task-tracker/LeadTrengoOrigin.vue'
import { normalizeLeadId } from '@/views/crm/task-tracker/leadFlowContext'

export default {
  name: 'LeadFlowModal',
  components: {
    LeadAssignManager,
    LeadTrengoOrigin,
    LeadContactability,
    LeadDoubleRequests,
    LeadFlowModalHeader,
    LeadFlowWorkspace,
    LeadOverview,
    LeadPersonalDetails
  },
  props: {
    aiOpen: { type: Boolean, default: false },
    leadId: {
      type: Number,
      required: true
    }
  },
  data() {
    return {
      detail: null,
      detailLoading: false,
      detailError: null,
      detailRequestGeneration: 0,
      contactability: null,
      contactabilityLoading: false,
      contactabilityError: null,
      contactabilityRequestGeneration: 0,
      doubleRequests: null,
      doubleRequestsError: null,
      doubleRequestsRequestGeneration: 0,
      assignmentPending: false,
      workspacePending: false,
      trengoSyncPending: false
    }
  },
  computed: {
    validLeadId() {
      return normalizeLeadId(this.leadId)
    },
    modalPending() {
      return this.workspacePending || this.trengoSyncPending || this.assignmentPending
    }
  },
  watch: {
    aiOpen() { this.$nextTick(this.syncDialogSemantics) }
  },
  // The page keys this component by lead ID, so each instance owns one lead.
  created() {
    if (!this.validLeadId) return
    this.loadDetail(this.validLeadId)
    this.loadContactability(this.validLeadId)
    this.loadDoubleRequests(this.validLeadId)
  },
  mounted() {
    if (this.validLeadId) this.$modal.show('lead-flow-workspace')
  },
  beforeDestroy() {
    this.invalidateRequests()
  },
  methods: {
    async loadDetail(leadId) {
      const generation = this.detailRequestGeneration + 1
      this.detailRequestGeneration = generation
      this.detailLoading = true
      this.detailError = null

      try {
        const { data } = await getLeadFlowDetail(leadId)
        if (generation !== this.detailRequestGeneration || leadId !== this.validLeadId) return
        this.detail = data
      } catch (error) {
        if (generation !== this.detailRequestGeneration || leadId !== this.validLeadId) return
        this.detail = null
        this.detailError = error
      } finally {
        if (generation === this.detailRequestGeneration && leadId === this.validLeadId) {
          this.detailLoading = false
        }
      }
    },
    async loadContactability(leadId) {
      const generation = this.contactabilityRequestGeneration + 1
      this.contactabilityRequestGeneration = generation
      this.contactabilityLoading = true
      this.contactabilityError = null

      try {
        const { data } = await getLeadFlowContactability(leadId)
        if (generation !== this.contactabilityRequestGeneration || leadId !== this.validLeadId) return
        this.contactability = data
      } catch (error) {
        if (generation !== this.contactabilityRequestGeneration || leadId !== this.validLeadId) return
        this.contactability = null
        this.contactabilityError = error
      } finally {
        if (generation === this.contactabilityRequestGeneration && leadId === this.validLeadId) {
          this.contactabilityLoading = false
        }
      }
    },
    async loadDoubleRequests(leadId) {
      const generation = this.doubleRequestsRequestGeneration + 1
      this.doubleRequestsRequestGeneration = generation
      this.doubleRequestsError = null

      try {
        const { data } = await getLeadFlowLeadDoubleRequests(leadId)
        if (generation !== this.doubleRequestsRequestGeneration || leadId !== this.validLeadId) return
        this.doubleRequests = data
      } catch (error) {
        if (generation !== this.doubleRequestsRequestGeneration || leadId !== this.validLeadId) return
        this.doubleRequestsError = error
      }
    },
    async onManagerAssigned() {
      await this.refreshWorkspace()
      this.$emit('board-reconcile')
    },
    onGuidanceChanged(data) {
      this.detail.management_guidance = data
      this.$emit('board-reconcile', { management_guidance_changed: true })
    },
    async refreshWorkspace() {
      if (!this.validLeadId) return
      const workspace = this.$refs.flowWorkspace

      await Promise.all([
        this.loadDetail(this.validLeadId),
        this.loadContactability(this.validLeadId),
        this.loadDoubleRequests(this.validLeadId),
        workspace ? workspace.refreshAll() : Promise.resolve()
      ])
    },
    async syncTrengoTail() {
      if (!this.validLeadId || this.trengoSyncPending) return
      this.trengoSyncPending = true

      try {
        const { data } = await postLeadFlowTrackerTrengoTailSync()
        if (this._isDestroyed) return
        await this.refreshWorkspace()
        if (this._isDestroyed) return
        let type = 'success'
        let title = 'Latest Trengo messages imported'
        if (data.skipped) {
          type = 'info'
          title = 'Trengo synchronization is already running'
        }
        this.$notify({
          type,
          title
        })
      } catch (error) {
        if (this._isDestroyed) return
        this.$notify({
          type: 'error',
          title: 'Unable to import latest Trengo messages'
        })
      } finally {
        this.trengoSyncPending = false
      }
    },
    close() {
      if (this.modalPending) return
      this.$emit('close')
    },
    onBeforeClose(event) {
      event.cancel()
      document.body.classList.add('vm--block-scroll')
      if (!this.aiOpen) this.close()
    },
    onOpened() {
      this.syncDialogSemantics()
      const closeButton = this.$refs.workspace ? this.$refs.workspace.querySelector('.lead-flow-modal-header__close') : null
      if (closeButton) closeButton.focus()
    },
    syncDialogSemantics() {
      // vue-js-modal hardcodes aria-modal; expose the adjacent chat to assistive technology.
      const dialog = this.$refs.leadDialog && this.$refs.leadDialog.$refs.modal
      if (dialog) dialog.setAttribute('aria-modal', String(!this.aiOpen))
    },
    invalidateRequests() {
      this.detailRequestGeneration += 1
      this.contactabilityRequestGeneration += 1
      this.doubleRequestsRequestGeneration += 1
    },
    updateLastActivity(value) {
      if (!value || !this.detail) return
      this.detail = {
        ...this.detail,
        last_activity_at: value
      }
    },
    updateCurrentStage(stage) {
      if (!stage || !this.detail) return
      this.detail = {
        ...this.detail,
        stage
      }
    }
  }
}
</script>

<style scoped>
.lead-flow-workspace-modal {
  overflow-x: hidden;
}

.lead-flow-workspace-modal :deep(.vm--modal) {
  height: 96vh !important;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  overflow: hidden !important;
  border: 1px solid #e6eaf0;
  border-radius: 18px;
  box-shadow: 0 22px 70px rgba(28, 45, 74, .2);
}

.lead-flow-modal {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  background: #f7f9fc;
  color: #17243a;
  font-family: 'CoreSansCR', sans-serif;
  font-size: 14px;
}

/* Keep inherited legacy sizes from overriding the shared Lead Flow theme. */
.lead-flow-modal :deep(p),
.lead-flow-modal :deep(span),
.lead-flow-modal :deep(time),
.lead-flow-modal :deep(button),
.lead-flow-modal :deep(a),
.lead-flow-modal :deep(input),
.lead-flow-modal :deep(textarea) {
  font-family: 'CoreSansCR', sans-serif;
}

.lead-flow-modal :deep(strong) {
  font-family: 'MazzardSoftH', sans-serif;
}

.lead-flow-modal :deep(.contactability__label) {
  font-size: 12px !important;
}

.lead-flow-modal :deep(.contactability__gmt) {
  font-size: 28px !important;
}

.lead-flow-modal :deep(.contactability__country),
.lead-flow-modal :deep(.contactability__date) {
  font-size: 14px !important;
}

.lead-flow-modal :deep(.contactability__clock) {
  font-size: 40px !important;
}

.lead-flow-modal :deep(.contactability__row-bottom) {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: end;
}

.lead-flow-modal :deep(.contactability__clock-wrap) {
  align-items: flex-start;
  flex-direction: column;
  gap: 4px;
}

.lead-flow-modal :deep(.contactability__date) {
  white-space: normal;
}

.lead-flow-modal :deep(.contactability__status) {
  font-size: 14px !important;
}

.lead-flow-modal :deep(.contactability__channel),
.lead-flow-modal :deep(.contactability__channel b) {
  font-size: 12px !important;
}

.lead-flow-modal :deep(.contactability__holiday) {
  font-size: 14px !important;
}

.lead-flow-modal :deep(.contactability) {
  width: 100%;
  max-width: none;
  margin-left: 0;
}

.lead-flow-modal__body {
  min-width: 0;
}

.lead-flow-modal__initial-state {
  margin: 20px 24px 32px;
}

.lead-flow-modal__overview {
  display: grid;
  min-width: 0;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  align-items: stretch;
  gap: 16px;
}

.lead-flow-modal__overview-details,
.lead-flow-modal__detail-error {
  grid-column: 1 / -1;
}

.lead-flow-modal__detail-error {
  display: grid;
  min-height: 300px;
  align-content: center;
  justify-items: center;
  padding: 28px;
  border: 1px solid #e3c7c2;
  border-radius: 16px;
  background: #fffaf8;
  color: #73413b;
  text-align: center;
}

.lead-flow-modal__contactability-state {
  min-height: 180px;
  padding: 24px;
  border: 1px solid #d8ded8;
  border-radius: 16px;
  background: #fff;
  color: #71807e;
}

.lead-flow-modal__contactability-state--error {
  display: grid;
  align-content: center;
  justify-items: center;
  gap: 12px;
  color: #73413b;
  text-align: center;
}

.lead-flow-modal__contactability-state--error button {
  min-height: 34px;
  padding: 0 15px;
  border: 1px solid #d5b7b1;
  border-radius: 8px;
  background: #fff;
  color: #925046;
  font-weight: 700;
  cursor: pointer;
}

.lead-flow-modal__detail-error strong {
  font-size: 16px;
}

.lead-flow-modal__detail-error p {
  margin: 7px 0 16px;
  color: #8e716d;
  font-size: 12px;
}

.lead-flow-modal__detail-error button {
  min-height: 34px;
  padding: 0 15px;
  border: 1px solid #d5b7b1;
  border-radius: 8px;
  background: #fff;
  color: #925046;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color .15s ease, border-color .15s ease;
}

.lead-flow-modal__detail-error button:hover,
.lead-flow-modal__detail-error button:focus-visible {
  border-color: #c9948b;
  background: #fff1ee;
}

@media (max-width: 900px) {
  .lead-flow-modal__overview {
    grid-template-columns: 1fr;
  }

  .lead-flow-modal__overview-details,
  .lead-flow-modal__detail-error {
    grid-column: auto;
  }
}

</style>

<style scoped>
@media (min-width: 1200px) {
  .lead-flow-workspace-modal--ai :deep(.vm--modal) {
    width: calc(100vw - 540px) !important;
    max-width: calc(100vw - 540px) !important;
    left: 12px !important;
  }
  .lead-flow-workspace-modal--ai :deep(.lead-flow-workspace) { grid-template-columns: 1fr; }
  .lead-flow-workspace-modal--ai :deep(.lead-flow-workspace__main),
  .lead-flow-workspace-modal--ai :deep(.lead-flow-workspace__side) { grid-column: auto; }
}
</style>
